import {
  extractDocumentReferenceAuthorData,
  hasValidProfessionalDocumentRefs,
} from "@/utils/fhirDocumentUtils";

/**
 * Monta objData + encounter no mesmo formato das Actions de reemissão,
 * para reutilizar hasValidProfessionalDocumentRefs sem duplicar a regra.
 */
function buildReissueRefs(
  row: any,
  practitionerRoleId: string | undefined,
) {
  const authorData = extractDocumentReferenceAuthorData(row?.fullResource);

  const objData = {
    organization: {
      id: authorData.organizationId,
      alias: authorData.organizationAlias,
      name: authorData.organizationName,
    },
    practitioner: {
      id: authorData.practitionerId,
      name: authorData.practitionerName,
    },
    servicePratice: authorData.practiceSetting,
    time: {
      start: row?.fullResource?.period?.start,
      end: row?.fullResource?.period?.end,
    },
    relatesTo: authorData.relatesTo,
    codeValue: row?.fullResource?.type?.coding?.[0]?.code,
    codeDisplay: row?.fullResource?.type?.coding?.[0]?.display,
    categoryValue: row?.fullResource?.category?.[0]?.coding?.[0]?.code,
    categoryDisplay: row?.fullResource?.category?.[0]?.coding?.[0]?.display,
  };

  const encounter = {
    id: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Encounter"))
      ?.reference?.split("/")?.[1],
    appointmentId: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Appointment"))
      ?.reference?.split("/")?.[1],
    requester: {
      reference: practitionerRoleId
        ? `PractitionerRole/${practitionerRoleId}`
        : undefined,
      display: authorData.practitionerName,
    },
    subject: row?.fullResource?.subject?.reference?.split("-")?.[1],
  };

  return { objData, encounter };
}

type ReissueContext = {
  accessType?: string;
  practitionerRoleId?: string;
};

/**
 * Espelha CertificateActions.canSign — atestado/relatório só reemitível
 * se o profissional logado for author (PractitionerRole) e refs forem válidas.
 */
export function canReissueCertificate(
  row: any,
  { accessType, practitionerRoleId }: ReissueContext,
): boolean {
  if (accessType !== "professional" || !row?.fullResource) return false;

  const isDocumentOwner =
    row.fullResource?.author?.some(
      (el: any) =>
        typeof el?.reference === "string" &&
        el.reference.includes("PractitionerRole/") &&
        el.reference.split("/")?.[1] === practitionerRoleId,
    ) ?? false;

  if (!isDocumentOwner) return false;

  const { objData, encounter } = buildReissueRefs(row, practitionerRoleId);
  return hasValidProfessionalDocumentRefs(objData, encounter);
}

/**
 * Espelha PrescriptionsActions.canSignOrReissue — receita só reemitível
 * se o requester do MedicationRequest for o PractitionerRole logado.
 */
export function canReissuePrescription(
  row: any,
  { accessType, practitionerRoleId }: ReissueContext,
): boolean {
  if (accessType !== "professional" || !row?.fullResource) return false;

  const requesterRoleId =
    row?.fullMedicationResource?.[0]?.resource?.requester?.reference?.split(
      "/",
    )?.[1];

  if (!practitionerRoleId || practitionerRoleId !== requesterRoleId) {
    return false;
  }

  const { objData, encounter } = buildReissueRefs(row, practitionerRoleId);
  return hasValidProfessionalDocumentRefs(objData, encounter);
}

/**
 * Espelha ServiceRequestActions.canSignOrReissue — solicitação só reemitível
 * se o requester do ServiceRequest for o PractitionerRole logado.
 */
export function canReissueSolicitation(
  row: any,
  { accessType, practitionerRoleId }: ReissueContext,
): boolean {
  if (accessType !== "professional" || !row?.fullResource) return false;

  const requesterRoleId =
    row?.fullServiceResource?.[0]?.resource?.requester?.reference?.split(
      "/",
    )?.[1];

  if (!practitionerRoleId || practitionerRoleId !== requesterRoleId) {
    return false;
  }

  const { objData, encounter } = buildReissueRefs(row, practitionerRoleId);
  return hasValidProfessionalDocumentRefs(objData, encounter);
}
